import fs from 'fs';
import path from 'path';
import { writeFile } from './fs';
import debounce from 'lodash.debounce';

interface CacheEntry<TValue> {
	value: TValue;
	expirationTime?: number;
}

interface Cache<TValue, KKey = string> {
	get(key: KKey): TValue | undefined;
	set(key: KKey, value: TValue, expirationTimeMs?: number): void;
	has(key: KKey): boolean;
	values(): IterableIterator<TValue>;
	size: number;
	delete(key: KKey): boolean;
	clear(): void;
}

export function createCache<TValue, KKey = string>(
	maxSize: number,
	cacheFileName: string
): Cache<TValue, KKey> {
	const cache = new Map<KKey, CacheEntry<TValue>>();

	let mutated = false;

	const cacheFilePath = path.join(
		process.cwd(),
		'.cache',
		process.env.NEXT_PUBLIC_LANGUAGE,
		process.env.NEXT_PUBLIC_TARGET_ENV,
		cacheFileName + '.json'
	);

	const readCache = () => {
		try {
			if (!fs.existsSync(cacheFilePath)) {
				return;
			}

			const data = fs.readFileSync(cacheFilePath, 'utf8');
			const parsedCache = JSON.parse(data);

			if (!Array.isArray(parsedCache)) {
				return;
			}

			for (let index = 0; index < parsedCache.length; index++) {
				const key = parsedCache[index][0];
				const entry = parsedCache[index][1];

				if (entry && typeof entry === 'object' && 'value' in entry) {
					cache.set(key as unknown as KKey, entry as CacheEntry<TValue>);
				} else {
					// Legacy format - just value
					cache.set(key as unknown as KKey, { value: entry as TValue });
				}
			}
		} catch (err) {
			// If the file doesn't exist or is invalid, start with an empty cache
		}
	};

	const isExpired = (entry: CacheEntry<TValue>): boolean => {
		if (!entry.expirationTime) {
			return false;
		}
		return Date.now() > entry.expirationTime;
	};

	const cleanupExpiredEntries = () => {
		for (const [key, entry] of cache.entries()) {
			if (isExpired(entry)) {
				cache.delete(key);
			}
		}
	};

	const writeCache = () => {
		if (!mutated) {
			return;
		}

		const cacheObject = Array.from(cache.entries());

		writeFile(cacheFilePath, JSON.stringify(cacheObject));
	};

	const debouncedWriteCache = debounce(writeCache, 400);

	const scheduleWrite = () => {
		mutated = true;
		debouncedWriteCache();
	};

	process.on('exit', writeCache);
	process.on('SIGINT', () => {
		writeCache();
		process.exit();
	});
	process.on('SIGTERM', () => {
		writeCache();
		process.exit();
	});

	process.on('beforeExit', writeCache);

	readCache();

	return {
		get(key: KKey): TValue | undefined {
			const entry = cache.get(key);

			if (!entry) {
				return undefined;
			}

			if (isExpired(entry)) {
				cache.delete(key);
				scheduleWrite();
				return undefined;
			}

			return entry.value;
		},
		set(key: KKey, value: TValue, expirationTimeMs?: number): void {
			if (cache.size >= maxSize && !cache.has(key)) {
				throw new Error(`Cache ${cacheFileName} is full`);
			}

			const entry: CacheEntry<TValue> = {
				value,
				expirationTime: expirationTimeMs ? Date.now() + expirationTimeMs : undefined,
			};

			cache.set(key, entry);
			scheduleWrite();
		},
		values(): IterableIterator<TValue> {
			cleanupExpiredEntries();

			const values: TValue[] = [];
			for (const entry of cache.values()) {
				if (!isExpired(entry)) {
					values.push(entry.value);
				}
			}
			return values[Symbol.iterator]();
		},
		has(key: KKey): boolean {
			const entry = cache.get(key);
			if (!entry) {
				return false;
			}

			if (isExpired(entry)) {
				cache.delete(key);
				scheduleWrite();
				return false;
			}

			return true;
		},
		delete(key: KKey): boolean {
			const result = cache.delete(key);
			if (result) {
				scheduleWrite();
			}
			return result;
		},
		clear(): void {
			cache.clear();
			scheduleWrite();
		},
		get size() {
			cleanupExpiredEntries();
			return cache.size;
		},
	};
}
